import type { ReactNode } from 'react'
import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { replaceWithRegion } from 'actions/NavigationActions'
import { fetchOrder } from 'actions/OrderActions'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import OhNoRefreshError from 'components/Common/ErrorBoundary/OhNoRefreshError'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isDeniedOrderAccessError } from 'lib/order/agentHubTravellerOrder'
import LoadingPage from 'tripPlanner/pages/Loading'

interface Props {
  children: ReactNode
}

function RestrictedOrderRouteGuard({ children }: Props) {
  const dispatch = useAppDispatch()
  const { orderId } = useParams<'orderId'>()
  const restrictedOrder = useAppSelector(state => orderId ? state.orders.agentHubTravellerOrders[orderId] : undefined)
  const commercialOrder = useAppSelector(state => orderId ? state.orders.orders[orderId] : undefined)
  const fetching = useAppSelector(state => orderId ? state.orders.ordersFetching[orderId] : false)
  const error = useAppSelector(state => orderId ? state.orders.orderErrors[orderId] : undefined)

  useEffect(() => {
    if (orderId && !restrictedOrder && commercialOrder?.type !== 'full') {
      dispatch(fetchOrder(orderId))
    }
  }, [dispatch, orderId, restrictedOrder, commercialOrder?.type])

  const retry = useCallback(() => {
    if (orderId) dispatch(fetchOrder(orderId, { force: true }))
  }, [dispatch, orderId])

  useEffect(() => {
    if (!orderId || !restrictedOrder) {
      return
    }

    dispatch(replaceWithRegion(`/account/my-escapes/order/${orderId}`))
  }, [dispatch, orderId, restrictedOrder])

  if (!orderId) {
    return <Error404PageWithOffers />
  }

  if (restrictedOrder) {
    return <LoadingPage />
  }

  if (error && isDeniedOrderAccessError(error)) {
    return <Error404PageWithOffers />
  }

  if (commercialOrder) {
    return children
  }

  if (error && !fetching) {
    return <OhNoRefreshError onRefresh={retry} />
  }

  return <LoadingPage />
}

export default RestrictedOrderRouteGuard
